/**
 * "PREFER SHARES TO PROPERTY?" — the one line a card carries when the same plan exists with the money
 * in the other kind of asset (owner, 2 Oct 2026).
 *
 * The engines model both: "sell the home and buy an investment property" and "sell the home and
 * invest in shares / managed funds" are separate candidates, scored on the same figures, and one of
 * them wins. Rather than claim they "come out about the same" — which is not always true: a property
 * pays stamp duty on day one and its rent is counted, shares are deemed — the card says the actual
 * gap, from the engine's own estate figures, and leaves the choice with the client. Only within a
 * small band does it say "about the same", because then it is.
 *
 * Pairs are found by candidate id, so a module need only name its ids here.
 */

const PAIRS: readonly [RegExp, string][] = [
    [/-abp-ip(-clear-debt)?$/, '-abp-invest$1'],
    [/-abp-invest(-clear-debt)?$/, '-abp-ip$1'],
    [/^dz-(rent-)?invest-property(-clear-debt)?$/, 'dz-$1invest$2'],
    [/^dz-(rent-)?invest(-clear-debt)?$/, 'dz-$1invest-property$2'],
    [/-sell-home-property(-clear-debt)?$/, '-sell-home-invest$1'],
    [/-sell-home-invest(-clear-debt)?$/, '-sell-home-property$1'],
    [/^gf-sell-property(-clear-debt)?$/, 'gf-sell-invest$1'],
    [/^gf-sell-invest(-clear-debt)?$/, 'gf-sell-property$1'],
];

const isProperty = (id: string) => /-abp-ip(-clear-debt)?$|invest-property|sell-home-property|gf-sell-property/.test(id);

/** The id of the same plan with the other asset, or null when this plan has no such twin. */
export function alternativeAssetId(id: string): string | null {
    for (const [re, to] of PAIRS) {
        if (re.test(id)) return id.replace(re, to);
    }
    return null;
}

export interface AlternativeAssetNote {
    /** "Prefer shares or managed funds to property?" */
    question: string;
    /** The sentence with the gap in it. */
    answer: string;
}

/**
 * The note for a candidate, or null. `estateOf` reads the figure the module measures estates by;
 * `years` is the horizon those figures are struck at.
 */
export function alternativeAssetNote<S extends { id?: string | null }>(
    strategy: S,
    all: readonly S[],
    estateOf: (s: S) => number | null | undefined,
    years: number,
): AlternativeAssetNote | null {
    const id = String(strategy.id || '');
    const twinId = alternativeAssetId(id);
    if (!twinId) return null;
    const twin = all.find((s) => String(s.id || '') === twinId);
    if (!twin) return null;
    const mine = estateOf(strategy);
    const theirs = estateOf(twin);
    if (mine == null || theirs == null || !Number.isFinite(mine) || !Number.isFinite(theirs)) return null;

    const property = isProperty(id);
    const question = property ? 'Prefer shares or managed funds to property?' : 'Prefer property to shares?';
    const other = property ? 'with the money in shares or managed funds instead' : 'buying an investment property instead';
    const gap = theirs - mine;
    const band = Math.max(10_000, Math.abs(mine) * 0.01);
    const money = `$${Math.round(Math.abs(gap)).toLocaleString('en-AU')}`;
    const horizon = `on the estate after ${years} years`;
    const answer =
        Math.abs(gap) <= band
            ? `The same plan ${other} comes out about the same ${horizon}. Pick whichever you're more comfortable holding.`
            : gap < 0
              ? `The same plan ${other} comes out about ${money} behind ${horizon}. Pick whichever you're more comfortable holding.`
              : `The same plan ${other} comes out about ${money} ahead ${horizon}. It won a different goal, which is why it isn't this card.`;
    return { question, answer };
}
